<template>
  <Modal
    v-model="open"
    :title="title"
    width="1000"
    class-name="area-add-update-modal"
    :mask-closable="false"
    @on-cancel="cancel('formItem')"
  >
    <Tabs v-model="activeTab" class="area-modal-tabs">
      <TabPane label="基本信息" name="basic">
        <Form
          ref="formItem"
          :model="formItem"
          :label-width="88"
          :rules="ruleValidate"
          class="basic-form"
        >
          <Row :gutter="16">
            <Col :span="12">
              <FormItem label="区域名称" prop="area_name">
                <Input v-model="formItem.area_name" placeholder="请输入区域名称" clearable />
              </FormItem>
            </Col>
            <Col :span="12">
              <FormItem label="客服电话" prop="phone">
                <Input v-model="formItem.phone" placeholder="请输入客服电话" clearable />
              </FormItem>
            </Col>
          </Row>
          <FormItem label="区域描述" prop="area_desc">
            <Input v-model="formItem.area_desc" placeholder="请输入区域描述" clearable />
          </FormItem>
          <div class="return-point-block">
            <div class="block-label">还车点</div>
            <FormItem label="地址" prop="returnPointAddress" :label-width="56">
              <div class="address-input-wrapper">
                <Input
                  ref="returnPointAddressInput"
                  v-model="returnPointAddress"
                  placeholder="输入关键词搜索还车点"
                  clearable
                  @on-focus="handleReturnPointAddressFocus"
                  @on-blur="handleReturnPointAddressBlur"
                  @on-change="handleReturnPointAddressChange"
                />
                <div
                  v-show="returnPointAddressSuggestions.length > 0 && showReturnPointSuggestions"
                  class="address-suggestions"
                >
                  <div
                    v-for="(item, index) in returnPointAddressSuggestions"
                    :key="index"
                    class="suggestion-item"
                    @mousedown.prevent="selectReturnPointAddress(item)"
                    @mouseenter="returnPointHoveredIndex = index"
                    @mouseleave="returnPointHoveredIndex = -1"
                    :class="{ hovered: returnPointHoveredIndex === index }"
                  >
                    <Icon type="ios-locate" style="margin-right: 8px; color: #1890ff;" />
                    <div class="suggestion-content">
                      <div class="suggestion-name">{{ item.name || '未知地点' }}</div>
                      <div class="suggestion-address">{{ item.district || '' }}{{ item.address || '' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </FormItem>
            <Row :gutter="12">
              <Col :span="12">
                <FormItem label="经度" prop="return_point" :label-width="56">
                  <Input
                    type="number"
                    v-model="formItem.return_point.coordinates[0]"
                    placeholder="经度"
                    clearable
                    @on-blur="reverseGeocodeReturnPoint"
                  />
                </FormItem>
              </Col>
              <Col :span="12">
                <FormItem label="纬度" :label-width="56">
                  <Input
                    type="number"
                    v-model="formItem.return_point.coordinates[1]"
                    placeholder="纬度"
                    clearable
                    @on-blur="reverseGeocodeReturnPoint"
                  />
                </FormItem>
              </Col>
            </Row>
            <div v-if="returnPointLocationAddress" class="coord-hint">
              <Icon type="ios-checkmark-circle" />
              {{ returnPointLocationAddress }}
            </div>
          </div>
        </Form>
      </TabPane>

      <TabPane label="区域编辑" name="edit" :disabled="!formItem.area_id">
        <div v-if="!formItem.area_id" class="edit-disabled-tip">
          请先保存区域基本信息，再导入车道并预览边界。
        </div>
        <div v-else class="area-edit-panel">
          <section class="section-card">
            <h4 class="section-title">导入车道轨迹</h4>
            <p class="section-hint">
              CSV 表头：poly_id, pt_idx, lat_gcj02, lon_gcj02（GCJ-02）。导入后自动生成区域边界（凸包）。
            </p>
            <div class="import-toolbar">
              <Upload
                ref="drivableCsvUpload"
                action
                :show-upload-list="false"
                accept=".csv,text/csv,text/plain"
                :before-upload="handleDrivableCsvFile"
              >
                <Button type="primary" ghost icon="md-cloud-upload">选择 CSV 文件</Button>
              </Upload>
              <div v-if="drivableCsvFileName" class="file-chip">
                <Icon type="ios-document" />
                <span class="file-name" :title="drivableCsvFileName">{{ drivableCsvFileName }}</span>
                <span v-if="drivableCsvFileSize" class="file-size">{{ drivableCsvFileSize }}</span>
                <Icon
                  type="ios-close-circle"
                  class="file-clear"
                  title="移除文件"
                  @click.native="clearDrivableFile"
                />
              </div>
              <span v-else class="file-placeholder">未选择文件</span>
            </div>
            <Row :gutter="16" class="params-row">
              <Col :span="12">
                <span class="param-label">抽稀容差(米)</span>
                <InputNumber v-model="drivableToleranceM" :min="0" :max="50" style="width: 120px" />
                <span class="param-extra">0 = 不抽稀</span>
              </Col>
              <Col :span="12">
                <span class="param-label">最大坐标点数</span>
                <InputNumber v-model="drivableMaxPoints" :min="500" :max="120000" style="width: 140px" />
              </Col>
            </Row>
            <div class="action-row">
              <Button
                type="primary"
                :loading="drivableImportLoading"
                :disabled="!drivableCsvText"
                @click="submitDrivableImport"
              >
                导入并覆盖
              </Button>
              <Button :loading="drivableClearLoading" @click="submitDrivableClear">清除车道与边界</Button>
            </div>
          </section>

          <section class="section-card map-section">
            <div class="map-header">
              <h4 class="section-title">区域边界预览</h4>
              <span v-if="boundaryPointCount > 0" class="map-meta">顶点 {{ boundaryPointCount }} 个</span>
            </div>
            <div class="map-wrap">
              <div ref="areaPreviewMap" class="area-preview-map" />
              <div v-if="!hasBoundary" class="map-empty">
                <Icon type="ios-map-outline" size="36" />
                <p>导入车道 CSV 后，将在此显示蓝色区域边界</p>
              </div>
            </div>
          </section>
        </div>
      </TabPane>
    </Tabs>
    <div slot="footer" class="modal-footer">
      <Button type="default" @click="cancel('formItem')">关闭</Button>
      <Button type="primary" :loading="loading" @click="ok('formItem')">提交</Button>
    </div>
  </Modal>
</template>
<script>
import {
  addArea,
  editArea,
  getAreaListByRole,
  getAreaDetail,
  importAreaDrivableCsv,
  clearAreaDrivable,
} from '@/api/area'
import { searchPlace, regeocode } from '@/api/geocoder'

const DEFAULT_MAP_CENTER = [116.397428, 39.90923]

export default {
  name: 'AreaAddUpdate',
  data() {
    return {
      activeTab: 'basic',
      formItem: {
        area_name: '',
        area_desc: '',
        phone: '',
        return_point: {
          type: 'Point',
          coordinates: ['', ''],
        },
      },
      ruleValidate: {
        area_name: [{ required: true, message: '请输入区域名称', trigger: 'blur' }],
        area_desc: [{ required: true, message: '请输入区域描述', trigger: 'blur' }],
        phone: [{ required: true, message: '请输入客服电话', trigger: 'blur' }],
        return_point: [
          {
            required: false,
            validator: (rule, value, callback) => {
              const validCoord = (v) => v !== '' && v !== null && v !== undefined && !isNaN(parseFloat(v))
              if (!value || !value.coordinates || !Array.isArray(value.coordinates) || value.coordinates.length < 2) {
                callback()
                return
              }
              if (!validCoord(value.coordinates[0]) || !validCoord(value.coordinates[1])) {
                callback(new Error('请输入有效的还车点经纬度'))
                return
              }
              callback()
            },
            trigger: 'blur',
          },
        ],
      },
      open: false,
      title: '添加区域',
      loading: false,
      drivableCsvText: '',
      drivableToleranceM: 2,
      drivableMaxPoints: 25000,
      drivableImportLoading: false,
      drivableClearLoading: false,
      drivableCsvFileName: '',
      drivableCsvFileSize: '',
      returnPointAddress: '',
      returnPointLocationAddress: '',
      returnPointGeocoding: false,
      returnPointAddressSuggestions: [],
      showReturnPointSuggestions: false,
      returnPointHoveredIndex: -1,
      returnPointSearchTimer: null,
      areaPreviewMap: null,
      areaPreviewPolygon: null,
      areaPreviewReturnMarker: null,
      mapInitTimer: null,
    }
  },
  computed: {
    hasBoundary() {
      return this.parseBoundaryRing().length >= 3
    },
    boundaryPointCount() {
      const ring = this.parseBoundaryRing()
      if (ring.length < 3) return 0
      const first = ring[0]
      const last = ring[ring.length - 1]
      const closed =
        ring.length > 1 &&
        Math.abs(first[0] - last[0]) < 1e-6 &&
        Math.abs(first[1] - last[1]) < 1e-6
      return closed ? ring.length - 1 : ring.length
    },
  },
  watch: {
    activeTab(val) {
      if (val === 'edit' && this.open) {
        this.scheduleMapInit()
      } else {
        this.destroyAreaPreviewMap()
      }
    },
    open(val) {
      if (!val) {
        this.destroyAreaPreviewMap()
      } else if (this.activeTab === 'edit') {
        this.scheduleMapInit()
      }
    },
  },
  methods: {
    scheduleMapInit() {
      if (this.mapInitTimer) clearTimeout(this.mapInitTimer)
      this.mapInitTimer = setTimeout(() => {
        this.mapInitTimer = null
        this.initAreaPreviewMap()
      }, 200)
    },

    parseBoundaryRing() {
      const b = this.formItem && this.formItem.boundary
      if (!b) return []
      if (b.coordinates && Array.isArray(b.coordinates)) {
        if (b.coordinates[0] && Array.isArray(b.coordinates[0][0])) {
          return b.coordinates[0]
        }
        if (Array.isArray(b.coordinates[0])) {
          return b.coordinates
        }
      }
      if (Array.isArray(b)) return b
      return []
    },

    destroyAreaPreviewMap() {
      if (this.areaPreviewPolygon) {
        try {
          this.areaPreviewPolygon.setMap(null)
        } catch (e) {}
        this.areaPreviewPolygon = null
      }
      if (this.areaPreviewReturnMarker) {
        try {
          this.areaPreviewReturnMarker.setMap(null)
        } catch (e) {}
        this.areaPreviewReturnMarker = null
      }
      if (this.areaPreviewMap) {
        try {
          this.areaPreviewMap.destroy()
        } catch (e) {}
        this.areaPreviewMap = null
      }
    },

    initAreaPreviewMap() {
      if (this.activeTab !== 'edit' || !this.open || !this.$refs.areaPreviewMap) return
      if (!window.AMap) {
        console.warn('高德地图未加载')
        return
      }
      this.destroyAreaPreviewMap()
      const ring = this.parseBoundaryRing()
      let center = DEFAULT_MAP_CENTER
      if (ring.length >= 1) {
        const lngs = ring.map((p) => Number(p[0])).filter((n) => !isNaN(n))
        const lats = ring.map((p) => Number(p[1])).filter((n) => !isNaN(n))
        if (lngs.length && lats.length) {
          center = [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2]
        }
      }
      this.areaPreviewMap = new window.AMap.Map(this.$refs.areaPreviewMap, {
        zoom: 14,
        center,
        resizeEnable: true,
      })
      this.areaPreviewMap.addControl(new window.AMap.Scale())
      this.areaPreviewMap.addControl(
        new window.AMap.ToolBar({ position: 'RB', liteStyle: true }),
      )
      this.renderBoundaryOnMap()
    },

    renderBoundaryOnMap() {
      if (!this.areaPreviewMap || !window.AMap) return
      if (this.areaPreviewPolygon) {
        this.areaPreviewPolygon.setMap(null)
        this.areaPreviewPolygon = null
      }
      if (this.areaPreviewReturnMarker) {
        this.areaPreviewReturnMarker.setMap(null)
        this.areaPreviewReturnMarker = null
      }

      const ring = this.parseBoundaryRing()
      if (ring.length >= 3) {
        const path = ring.map((c) => new window.AMap.LngLat(Number(c[0]), Number(c[1])))
        this.areaPreviewPolygon = new window.AMap.Polygon({
          path,
          strokeColor: '#1890ff',
          strokeWeight: 2,
          strokeOpacity: 1,
          fillColor: '#1890ff',
          fillOpacity: 0.25,
          zIndex: 10,
        })
        this.areaPreviewPolygon.setMap(this.areaPreviewMap)
        this.areaPreviewMap.setFitView([this.areaPreviewPolygon], false, [48, 48, 48, 48])
      }

      const rp = this.formItem.return_point
      if (rp && rp.coordinates && rp.coordinates.length >= 2) {
        const lng = parseFloat(rp.coordinates[0])
        const lat = parseFloat(rp.coordinates[1])
        if (!isNaN(lng) && !isNaN(lat)) {
          this.areaPreviewReturnMarker = new window.AMap.Marker({
            position: [lng, lat],
            title: '还车点',
            zIndex: 20,
          })
          this.areaPreviewReturnMarker.setMap(this.areaPreviewMap)
        }
      }
    },

    formatFileSize(bytes) {
      if (!bytes || bytes <= 0) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    },

    clearDrivableFile() {
      this.drivableCsvText = ''
      this.drivableCsvFileName = ''
      this.drivableCsvFileSize = ''
      try {
        if (this.$refs.drivableCsvUpload && this.$refs.drivableCsvUpload.clearFiles) {
          this.$refs.drivableCsvUpload.clearFiles()
        }
      } catch (e) {}
    },

    async refreshAreaBoundary() {
      const aid = this.formItem.area_id != null ? this.formItem.area_id : this.formItem.areaId
      if (aid == null || aid === '') return
      try {
        const res = await getAreaDetail({ area_id: aid })
        const body = res && res.data
        if (body && body.code === 200 && body.data) {
          if (body.data.boundary) {
            this.$set(this.formItem, 'boundary', body.data.boundary)
          } else {
            this.$delete(this.formItem, 'boundary')
          }
          if (body.data.center) {
            this.$set(this.formItem, 'center', body.data.center)
          }
          this.$nextTick(() => {
            if (this.areaPreviewMap) {
              this.renderBoundaryOnMap()
            } else {
              this.scheduleMapInit()
            }
          })
        }
      } catch (e) {
        console.error('刷新区域边界失败', e)
      }
    },

    init(params) {
      this.activeTab = 'basic'
      this.clearDrivableFile()
      this.destroyAreaPreviewMap()

      if (params) {
        this.title = '修改区域'
        this.formItem = JSON.parse(JSON.stringify(params))
        if (params.areaName !== undefined && !this.formItem.area_name) {
          this.formItem.area_name = params.areaName
        }
        if (params.areaDesc !== undefined && !this.formItem.area_desc) {
          this.formItem.area_desc = params.areaDesc
        }
        if (params.return_point && params.return_point.coordinates) {
          this.formItem.return_point = {
            type: 'Point',
            coordinates: params.return_point.coordinates,
          }
          if (params.return_point.coordinates[0] && params.return_point.coordinates[1]) {
            this.loadReturnPointAddressFromCoordinates(params.return_point.coordinates)
          }
        } else if (!this.formItem.return_point || !this.formItem.return_point.coordinates) {
          this.formItem.return_point = { type: 'Point', coordinates: ['', ''] }
        }
        this.returnPointAddressSuggestions = []
        this.showReturnPointSuggestions = false
        if (this.formItem.area_id) {
          this.refreshAreaBoundary()
        }
      } else {
        this.title = '添加区域'
        this.formItem = {
          area_name: '',
          area_desc: '',
          phone: '',
          return_point: { type: 'Point', coordinates: ['', ''] },
        }
      }
      this.open = true
    },

    handleDrivableCsvFile(file) {
      const maxBytes = 12 * 1024 * 1024
      if (!file || !file.size) {
        this.$Message.warning('文件无效')
        return false
      }
      if (file.size > maxBytes) {
        this.$Message.error('文件过大，请控制在 12MB 以内')
        return false
      }
      const name = file.name || ''
      const lower = name.toLowerCase()
      if (lower && !lower.endsWith('.csv') && file.type !== 'text/csv' && file.type !== 'text/plain') {
        this.$Message.warning('请选择 .csv 文件')
        return false
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = (e.target && e.target.result) || ''
        if (!String(text).trim()) {
          this.$Message.error('文件内容为空')
          return
        }
        this.drivableCsvText = text
        this.drivableCsvFileName = name || 'lane.csv'
        this.drivableCsvFileSize = this.formatFileSize(file.size)
        this.$Message.success('已选择文件，点击「导入并覆盖」写入车道并生成边界')
        this.$nextTick(() => {
          try {
            if (this.$refs.drivableCsvUpload && this.$refs.drivableCsvUpload.clearFiles) {
              this.$refs.drivableCsvUpload.clearFiles()
            }
          } catch (err) {}
        })
      }
      reader.onerror = () => this.$Message.error('读取文件失败')
      reader.readAsText(file, 'UTF-8')
      return false
    },

    async submitDrivableImport() {
      const aid = this.formItem.area_id != null ? this.formItem.area_id : this.formItem.areaId
      if (aid == null || aid === '') {
        this.$Message.warning('请先保存区域')
        return
      }
      if (!this.drivableCsvText || !String(this.drivableCsvText).trim()) {
        this.$Message.warning('请先选择 CSV 文件')
        return
      }
      this.drivableImportLoading = true
      try {
        const res = await importAreaDrivableCsv({
          area_id: aid,
          csvText: String(this.drivableCsvText),
          tolerance_m: this.drivableToleranceM,
          max_points: this.drivableMaxPoints,
        })
        const body = res && res.data
        if (body && body.code === 200) {
          const pc = body.data && body.data.point_count != null ? body.data.point_count : '—'
          const bp = body.data && body.data.boundary_points != null ? body.data.boundary_points : '—'
          this.$Message.success(`导入成功：车道点 ${pc}，边界顶点 ${bp}`)
          delete this.formItem.drivable_centerlines
          delete this.formItem.drivable_centerlines_meta
          await this.refreshAreaBoundary()
        } else {
          this.$Message.error((body && body.msg) || '导入失败')
        }
      } catch (e) {
        this.$Message.error((e && e.message) || '请求失败')
      } finally {
        this.drivableImportLoading = false
      }
    },

    async submitDrivableClear() {
      const aid = this.formItem.area_id != null ? this.formItem.area_id : this.formItem.areaId
      if (aid == null || aid === '') return
      this.drivableClearLoading = true
      try {
        const res = await clearAreaDrivable({ area_id: aid })
        const body = res && res.data
        if (body && body.code === 200) {
          this.$Message.success('已清除车道与区域边界')
          delete this.formItem.drivable_centerlines
          delete this.formItem.drivable_centerlines_meta
          delete this.formItem.boundary
          delete this.formItem.bounds
          this.renderBoundaryOnMap()
        } else {
          this.$Message.error((body && body.msg) || '失败')
        }
      } catch (e) {
        this.$Message.error((e && e.message) || '请求失败')
      } finally {
        this.drivableClearLoading = false
      }
    },

    ok(name) {
      this.$refs[name].validate((valid) => {
        if (!valid) return
        const postData = JSON.parse(JSON.stringify(this.formItem))
        delete postData.drivable_centerlines
        delete postData.drivable_centerlines_meta
        delete postData.boundary
        delete postData.bounds
        delete postData.center
        if (postData.return_point && postData.return_point.coordinates) {
          postData.return_point.coordinates = postData.return_point.coordinates.map((coord) =>
            coord === '' ? '' : parseFloat(coord),
          )
          const [lng, lat] = postData.return_point.coordinates
          if (lng === '' || lat === '' || isNaN(lng) || isNaN(lat)) {
            delete postData.return_point
          }
        }
        if (this.formItem.areaId || this.formItem.area_id || this.formItem._id) {
          editArea({ ...postData, area_id: this.formItem.area_id || this.formItem.areaId })
            .then((res) => {
              if (res.data.code === 200) {
                this.$Message.success('区域修改成功')
                this.cancel('formItem')
                this.$emit('reload')
                this.updateAreaStore()
              } else {
                this.$Message.error(res.data.msg)
              }
            })
            .catch((err) => this.$Message.error(err))
        } else {
          addArea(postData)
            .then((res) => {
              if (res.data.code === 200) {
                this.$Message.success('区域添加成功，请进入「区域编辑」导入车道 CSV')
                this.cancel('formItem')
                this.$emit('reload')
                this.updateAreaStore()
              } else {
                this.$Message.error(res.data.msg)
              }
            })
            .catch((err) => this.$Message.error(err))
        }
      })
    },

    cancel(name) {
      this.open = false
      this.destroyAreaPreviewMap()
      this.$nextTick(() => {
        this.formItem = {
          area_name: '',
          area_desc: '',
          phone: '',
          return_point: { type: 'Point', coordinates: ['', ''] },
        }
        this.activeTab = 'basic'
        this.clearDrivableFile()
        this.returnPointAddress = ''
        this.returnPointLocationAddress = ''
        this.returnPointAddressSuggestions = []
        this.showReturnPointSuggestions = false
        if (this.$refs[name]) this.$refs[name].resetFields()
      })
    },

    handleReturnPointAddressFocus() {
      if (this.returnPointAddress && this.returnPointAddress.trim()) {
        this.showReturnPointSuggestions = true
        this.searchReturnPointAddressSuggestions(this.returnPointAddress)
      } else {
        this.showReturnPointSuggestions = false
      }
    },

    handleReturnPointAddressBlur() {
      setTimeout(() => {
        this.showReturnPointSuggestions = false
      }, 200)
    },

    handleReturnPointAddressChange(value) {
      if (this.returnPointSearchTimer) clearTimeout(this.returnPointSearchTimer)
      let keyword = typeof value === 'string' ? value : (value && value.target && value.target.value) || this.returnPointAddress || ''
      keyword = String(keyword || '')
      if (!keyword.trim()) {
        this.returnPointAddressSuggestions = []
        this.showReturnPointSuggestions = false
        return
      }
      this.returnPointSearchTimer = setTimeout(() => {
        this.searchReturnPointAddressSuggestions(keyword.trim())
      }, 300)
    },

    async searchReturnPointAddressSuggestions(keyword) {
      if (!keyword || !keyword.trim()) return
      this.returnPointGeocoding = true
      this.showReturnPointSuggestions = true
      try {
        const res = await searchPlace({ keyword: keyword.trim() })
        this.returnPointGeocoding = false
        if (res.data.code === 200 && res.data.data && res.data.data.length > 0) {
          this.returnPointAddressSuggestions = res.data.data
          this.showReturnPointSuggestions = true
        } else {
          this.returnPointAddressSuggestions = []
          this.showReturnPointSuggestions = false
        }
      } catch (error) {
        this.returnPointGeocoding = false
        this.$Message.error('地址搜索失败')
        this.returnPointAddressSuggestions = []
        this.showReturnPointSuggestions = false
      }
    },

    selectReturnPointAddress(item) {
      if (item && item.location) {
        this.returnPointAddress = item.formattedAddress || item.name
        this.$set(this.formItem.return_point.coordinates, 0, item.location.lng.toFixed(6))
        this.$set(this.formItem.return_point.coordinates, 1, item.location.lat.toFixed(6))
        this.returnPointLocationAddress = item.formattedAddress
        this.showReturnPointSuggestions = false
        this.returnPointAddressSuggestions = []
        this.$Message.success('还车点已选择')
      }
    },

    async reverseGeocodeReturnPoint() {
      const lng = this.formItem.return_point.coordinates[0]
      const lat = this.formItem.return_point.coordinates[1]
      if (!lng || !lat || lng === '' || lat === '') {
        this.returnPointLocationAddress = ''
        return
      }
      const lngNum = parseFloat(lng)
      const latNum = parseFloat(lat)
      if (isNaN(lngNum) || isNaN(latNum)) return
      try {
        const res = await regeocode({ lng: lngNum, lat: latNum })
        if (res.data.code === 200 && res.data.data && res.data.data.formattedAddress) {
          this.returnPointLocationAddress = res.data.data.formattedAddress
          if (!this.returnPointAddress) this.returnPointAddress = res.data.data.formattedAddress
        }
      } catch (e) {}
    },

    async loadReturnPointAddressFromCoordinates(coordinates) {
      const lng = parseFloat(coordinates[0])
      const lat = parseFloat(coordinates[1])
      if (isNaN(lng) || isNaN(lat)) return
      try {
        const res = await regeocode({ lng, lat })
        if (res.data.code === 200 && res.data.data && res.data.data.formattedAddress) {
          this.returnPointAddress = res.data.data.formattedAddress
          this.returnPointLocationAddress = res.data.data.formattedAddress
        }
      } catch (e) {}
    },

    async updateAreaStore() {
      try {
        const res = await getAreaListByRole()
        if (res.data.code === 200) {
          this.$store.commit('setareas', JSON.stringify(res.data.data))
        }
      } catch (e) {}
    },
  },
  beforeDestroy() {
    if (this.returnPointSearchTimer) clearTimeout(this.returnPointSearchTimer)
    if (this.mapInitTimer) clearTimeout(this.mapInitTimer)
    this.destroyAreaPreviewMap()
  },
}
</script>

<style lang="less" scoped>
.area-modal-tabs {
  /deep/ .ivu-tabs-content {
    padding-top: 4px;
  }
}

.basic-form {
  padding: 4px 0 8px;
}

.return-point-block {
  margin-top: 8px;
  padding: 14px 16px 4px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;

  .block-label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }

  .coord-hint {
    color: #52c41a;
    font-size: 12px;
    margin: -4px 0 8px 56px;
  }
}

.edit-disabled-tip {
  padding: 24px;
  text-align: center;
  color: #999;
  background: #fafafa;
  border-radius: 6px;
}

.area-edit-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-card {
  padding: 16px 18px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.section-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.section-hint {
  margin: 0 0 14px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.5;
}

.import-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 420px;
  padding: 6px 10px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  color: #1890ff;
  font-size: 13px;

  .file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
  }

  .file-size {
    color: #69c0ff;
    font-size: 12px;
    flex-shrink: 0;
  }

  .file-clear {
    cursor: pointer;
    color: #8c8c8c;
    flex-shrink: 0;
    &:hover {
      color: #ff4d4f;
    }
  }
}

.file-placeholder {
  font-size: 13px;
  color: #bfbfbf;
}

.params-row {
  margin-bottom: 14px;

  .param-label {
    display: inline-block;
    width: 100px;
    font-size: 13px;
    color: #595959;
  }

  .param-extra {
    margin-left: 8px;
    font-size: 12px;
    color: #bfbfbf;
  }
}

.action-row {
  display: flex;
  gap: 10px;
}

.map-section {
  padding-bottom: 12px;
}

.map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  .section-title {
    margin-bottom: 0;
  }

  .map-meta {
    font-size: 12px;
    color: #8c8c8c;
  }
}

.map-wrap {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.area-preview-map {
  width: 100%;
  height: 380px;
  background: #f5f5f5;
}

.map-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(250, 250, 250, 0.92);
  color: #bfbfbf;
  pointer-events: none;
  z-index: 2;

  p {
    margin-top: 10px;
    font-size: 13px;
  }
}

.modal-footer {
  text-align: center;
}

.address-input-wrapper {
  position: relative;
  width: 100%;

  .address-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #dcdee2;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1100;
    max-height: 240px;
    overflow-y: auto;
    margin-top: 4px;

    .suggestion-item {
      padding: 10px 14px;
      cursor: pointer;
      display: flex;
      align-items: flex-start;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      &:hover,
      &.hovered {
        background-color: #f5f5f5;
      }

      .suggestion-name {
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }

      .suggestion-address {
        font-size: 12px;
        color: #999;
      }
    }
  }
}
</style>
